import Request from './Request'
import Response from './Response'
import AccessToken from './AccessToken'

import clonedeep from 'lodash.clonedeep'


/**
 * The API explorer logic
 */
class Engine {
  /**
   * Initialize the explorer to a default state
   */
  constructor({
    endpoint,
    credentials,
    setCredentials,
    microcopy,
    useState,
    customClientName = 'credentials',
    customClientId,
    customClientSecret
  }) {
    this.endpoint = endpoint
    this.requiredParams = clonedeep(endpoint?.params || []).filter(onlyRequired)

    this.microcopy = microcopy
    this.accessToken = new AccessToken({
      credentials,
      setCredentials,
      customClientName,
      customClientId,
      customClientSecret,
    })
    // create an alert object
    this.useState = useState

    // register the active endpoint
    this.initState('Active', false)
    this.initState('Enabled', false)
    this.initState('Values', this.defaultValues())
    this.initState('Valid', false)
    this.initState('Executing', false)
    this.initState('RenderedResponse', null)
    this.initState('RenderedHeaders', null)
    this.initState('StatusCode', null)
    this.initState('Success', false)
    this.initState('Alert', null)
  }

  // Disables the API explorer, hiding it in the UI
  disable = () => { this.setEnabled(false) }

  // Disables the API explorer, hiding it in the UI
  enable = () => { this.setEnabled(true) }

  // Toggles the API explorer
  toggle = () => { 
    this.setEnabled(enabled => !enabled)
  }

  // determines if a param needs to be autofocused
  fieldNeedsFocus(id) {
    if (this.requiredParams.length < 1) { return false }
    return this.requiredParams[0].id === id
  }

  // Set a new parameter value and validate the explorer
  setParam(location, namespace, name, value) {
    let values = { ...this.values }
    this.updateValues(values[location], [...namespace, name], value)
    this.setValues(values)
    this.validate()
  }

  // Executes an API call
  async execute() {
    // show a message if the form is not valid
    if (!this.valid) {
      return this.setAlert({ message: this.microcopy.text('explorer.messages.missing_params'), type: 'warning' })
    }

    // set the UI to executing and clear previous response values
    this.setExecuting(true)
    this.setRenderedResponse(null)
    this.setRenderedHeaders(null)
    this.setStatusCode(null)

    // make the API call and process the response
    const request = new Request(this)
    await request.init()

    const htmlResponse = await request.call()

    // if there was no html response, an unknow error
    // occured. Probably CORS related
    if (!htmlResponse) {
      this.setExecuting(false)
      this.setSuccess(false)
      this.setAlert({ message: 'An unknown error occured', type: 'error' })
      return
    }

    // process the response and show a message
    new Response(this, htmlResponse).process()
    this.setAlert({ message: `[${htmlResponse.status}] ${this.microcopy.text('explorer.messages.response_received')}`, 
      type: htmlResponse.ok ? 'success' : 'warning'
    })   
  }

  // PRIVATE

  // Initializes a state using the useState hook
  initState(key, defaultValue) {
    const [value, setter] = this.useState(defaultValue)
    const lowerCaseKey = key.charAt(0).toLowerCase() + key.slice(1)
    this[lowerCaseKey] = value
    this[`set${key}`] = setter
  }

  // sets the default values for a endpoint
  defaultValues() {
    const values = {
      header: {},
      path: {},
      body: {},
      query: {}
    }

    this.requiredParams.map(({ namespace, key, location, example}) => {
      this.updateValues(values[location], [...namespace, key], example)
    })

    return values
  }

  // Checks of the current params for an endpoint are valid
  validate() {
    const valid = this.requiredParams.every(({ namespace, key, location}) => {
      const value = this.valueFor(this.values[location], [...namespace, key])
      return ![undefined, null, ''].includes(value)
    })
    this.setValid(valid)
  }

  // smart merges in the new nested field 
  updateValues(values, [key, ...keys], value) {
    if (keys.length) {
      values[key] = {}
      this.updateValues(values[key], keys, value)
    } else {
      values[key] = value
    }
  }

  // gets the value for a given key
  valueFor(values, [key, ...keys]) {
    if (keys.length) { return this.valueFor(values[key], keys)}
    else { return values[key] }
  }
}

// filters only required params, nested
const onlyRequired = param => {
  if (param.params) {
    param.params = param.params.filter(onlyRequired)
  }
  return param.required
}

export default Engine
export { default as AccessToken } from './AccessToken'
